import { Box, Card, Divider, Typography } from '@mui/material'
import MonetaryFormat from 'components/MonetaryFormat/MonetaryFormat'
import Cart from 'views/Sale/SaleUI/Cart/Cart'
import { getOrderFulfillmentType } from 'components/Sell/OrderLookup/MapFulfillmentTypeIcon'
import { useTranslation } from 'react-i18next'
import { DataDisplayLeftRight } from './DataDisplayLeftRight'
import usePaymentContext from '../../hook/usePaymentContext'
import { useMockOrderLineToOrderItems } from 'components/Sell/OrderLookup/OrderInfo/buildOrderToOrderViewModel'
import { useMemo } from 'react'
import useGetSellDeps from 'components/Sell/hook/useGetSellDeps'
import {
    convertToItemViewModel,
    OrderItemViewModel,
} from 'store/models/sellModels'
import { ItemType } from 'models'
import { values } from 'lodash-es'

export function OrderDetail() {
    const { orderDetail } = usePaymentContext()
    const { getCartDependencies } = useGetSellDeps()
    const mockOrderLineToOrderItems = useMockOrderLineToOrderItems()

    const paymentOrderDetail = useMemo(() => {
        const orderLines =
            orderDetail?.orderLines
                ?.map((l, _, lines) => {
                    return {
                        ...l,
                        subOrderLines:
                            lines.filter(
                                (a) => a.refOrderLineID === l.orderLineID,
                            ) ?? [],
                    }
                })
                ?.filter((a) => !a.refOrderLineID) ?? []
        const { removedData, orderItems } = mockOrderLineToOrderItems(
            orderLines,
        )

        let orderItemsList: OrderItemViewModel[] = []
        if (
            orderItems.some(
                (s) =>
                    s.item.type === ItemType.ProductVariant &&
                    Object.keys(s.usedVariants).length === 0,
            )
        ) {
            orderItemsList = []
        } else {
            orderItemsList = orderItems.map((m) => {
                let orderlineSelected = orderLines.find(
                    (f) =>
                        f.item?.baseItemID === m.item.itemId &&
                        f.orderLineID === m.id,
                )
                return {
                    ...m,
                    item: {
                        ...m.item,
                        variantGroups: orderlineSelected?.item?.baseItem
                            ? values(m.usedVariants).map((_) => {
                                  return { variantOptions: [] }
                              })
                            : [],
                        variations: orderlineSelected?.item?.baseItem
                            ? [
                                  {
                                      ...convertToItemViewModel(
                                          orderlineSelected,
                                      ),
                                      combinationIds: [
                                          ...(orderlineSelected?.item?.combinationIndices?.split(
                                              '*',
                                          ) ?? ''),
                                      ],
                                  },
                              ]
                            : [],
                    },
                }
            })
        }

        return {
            orderNo: orderDetail?.orderNo ?? 'New Order',
            serviceChargeAmount: orderDetail?.serviceChargeAmount ?? 0,
            totalTaxAmount: orderDetail?.totalTaxAmount ?? 0,
            totalDueAmount: orderDetail?.totalDueAmount ?? 0,
            subtotal: orderDetail?.subtotal ?? 0,
            discountAmount: orderDetail?.discountAmount ?? 0,
            removedData,
            orderItems: orderItemsList,
        }
    }, [
        mockOrderLineToOrderItems,
        orderDetail?.discountAmount,
        orderDetail?.orderLines,
        orderDetail?.orderNo,
        orderDetail?.serviceChargeAmount,
        orderDetail?.subtotal,
        orderDetail?.totalDueAmount,
        orderDetail?.totalTaxAmount,
    ])
    const { t } = useTranslation()
    const orderType = getOrderFulfillmentType().find(
        (a) => a.type === orderDetail?.orderFulfillmentTypeID,
    )
    const cartDeps = useMemo(
        () =>
            getCartDependencies(
                undefined,
                () => {},
                paymentOrderDetail?.removedData ?? {},
            ),
        [getCartDependencies, paymentOrderDetail?.removedData],
    )
    const displayAmountInfo = (label: string, value: number = 0) => {
        if (value === 0) return null
        return (
            <>
                <Divider />
                <Box p={2}>
                    <DataDisplayLeftRight
                        left={`${label}: `}
                        right={<MonetaryFormat value={value} isOnlyString />}
                    />
                </Box>
            </>
        )
    }
    return (
        <Card variant="outlined" sx={{ overflow: 'auto' }}>
            <Box p={2}>
                <DataDisplayLeftRight
                    left={
                        <Typography fontWeight="bold">
                            {t('finish.label.order')}:{' '}
                            {paymentOrderDetail?.orderNo ?? 'loading'}
                        </Typography>
                    }
                    right={
                        <Typography fontWeight="bold">
                            {orderType?.label || ''}
                        </Typography>
                    }
                />
            </Box>
            <Divider />
            <Box p={1}>
                <Cart
                    items={paymentOrderDetail.orderItems}
                    isReadonly={true}
                    dependencies={{
                        ...cartDeps,
                    }}
                    isShownInSplitPayment={true}
                />
            </Box>
            {displayAmountInfo(
                t('finish.label.sub_total'),
                paymentOrderDetail?.subtotal,
            )}
            {displayAmountInfo(
                t('finish.label.discount'),
                paymentOrderDetail?.discountAmount,
            )}
            {displayAmountInfo(
                t('finish.label.service_charge'),
                paymentOrderDetail?.serviceChargeAmount,
            )}
            {displayAmountInfo(
                t('finish.label.tax'),
                paymentOrderDetail?.totalTaxAmount,
            )}
            <Divider />
            <Box p={2}>
                <DataDisplayLeftRight
                    left={
                        <Typography fontWeight="bold">
                            {t('finish.label.total_due')}:{' '}
                        </Typography>
                    }
                    right={
                        <Typography fontWeight="bold">
                            <MonetaryFormat
                                value={paymentOrderDetail?.totalDueAmount ?? 0}
                                isOnlyString
                            />
                        </Typography>
                    }
                />
            </Box>
        </Card>
    )
}
